import React, { useContext, useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Slider } from "@mui/material";
import moment from 'moment';
import Icon from "../../components/atoms/Icon.js";
import AuthContext from '../../contexts/auth-context.js';
import MiniMapZone from './MiniMapZone.js';
import { findIntelligentCutoff, roundToNextHour } from '../../util.js';
import { formatClock } from '../Concession/live/liveMetrics';
import '../Concession/concession.css';
import './retail.css';
import { zoneHeatColor, dwellHeatColor } from './retailPalette';

const RetailMap = ({
  currentLocation,
  data,
  currentTime,
  setCurrentTime,
  isToday,
  dayType,
  currentGame,
  peak,
  avg,
  findLabelByZoneId,
  getCurrentMinute,
  getGatesOpenTime,
  fetchData,
  setOccupancyAutoFetchEnabled,
  occupancyAutoFetchDidComplete,
  setOccupancyAutoFetchDidComplete,
  isAnalyticsView,
  queueTimeData,
  queueTimeDataMiniMap,
  transactionTimeData,
  transactionTimeDataMiniMap
}) => {
  const authCtx = useContext(AuthContext);
  const [loadedComponent, setLoadedComponent] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [occupancyAutoRefreshEnabled, setOccupancyAutoRefreshEnabled] = useState(false);
  const [averageData, setAverageData] = useState(null);
  const [cutoffTime, setCutoffTime] = useState(null);
  const [analyticsConfig, setAnalyticsConfig] = useState(null);
  const [zoneDwellEstimates, setZoneDwellEstimates] = useState({});

  // State to track which zone is being hovered
  const [hoveredZoneId, setHoveredZoneId] = useState(null);
  
  // Refs for scrolling to the hovered zone
  const zoneListRef = useRef(null);
  const zoneRefs = useRef({});

  // Time dock: floating while pinned to the screen, settled once the page reaches its spot.
  // A marker right after the dock is visible only when the dock sits in its natural place.
  const [dockSettled, setDockSettled] = useState(false);
  const dockObserver = useRef(null);
  const dockSentinelRef = useCallback((node) => {
    if (dockObserver.current) {
      dockObserver.current.disconnect();
      dockObserver.current = null;
    }
    if (node && typeof IntersectionObserver !== 'undefined') {
      dockObserver.current = new IntersectionObserver(([entry]) => setDockSettled(entry.isIntersecting), { threshold: 0 });
      dockObserver.current.observe(node);
    }
  }, []);

  // Live layout: how tall the map can be so the
  // whole Map tab (map, rail and time bar) fits on screen without scrolling
  const heroRef = useRef(null);
  const [mapFit, setMapFit] = useState(null);
  useLayoutEffect(() => {
    if (isAnalyticsView) return undefined;
    const measure = () => {
      const el = heroRef.current;
      if (!el || typeof window === 'undefined') return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Leave room for the hero's padding and the time bar under it
      const available = window.innerHeight - top - 48 - 132;
      setMapFit(Math.max(320, Math.min(760, Math.round(available))));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isAnalyticsView, loadedComponent]);

  // Play: steps the slider forward on its own until it reaches the end
  const [playing, setPlaying] = useState(false);
  const playRef = useRef(null);
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      const p = playRef.current;
      if (!p) return;
      const next = Math.min(p.max, (p.time ?? p.min) + p.step);
      p.change(null, next);
      if (next >= p.max) setPlaying(false);
    }, 100);
    return () => clearInterval(id);
  }, [playing]);

  // ---- Live layout: per-zone peaks (before any early return) ----
  const rowTime = (row, minute) => {
    const t = row?.time2 ? new Date(row.time2).getTime() : NaN;
    return Number.isNaN(t) ? minute * 60000 : t;
  };
  const zonePeaks = useMemo(() => {
    const peaks = {};
    if (!data) return peaks;
    for (let m = 0; m < data.length; m++) {
      (data[m]?.traffic || []).forEach((z) => {
        if (!peaks[z.id] || (z.traffic || 0) > peaks[z.id].value) {
          peaks[z.id] = { value: z.traffic || 0, t: rowTime(data[m], m) };
        }
      });
    }
    return peaks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Calculate doors open time
  const dayIndex = isToday ? new Date().getDay() : new Date(currentGame?.date?.replace(/-0(\d)/g, '-$1')).getDay();
  const doorsOpenTimeInMins = dayType === "game" ? 600 : currentLocation.doorsOpenTime[dayIndex];

  // Function to handle zone hover from map component
  const handleZoneHover = (zoneId) => {
    setHoveredZoneId(zoneId);
    
    if (zoneId && zoneRefs.current[zoneId] && zoneListRef.current) {
      const zoneElement = zoneRefs.current[zoneId];
      const listContainer = zoneListRef.current;
      
      const elementRect = zoneElement.getBoundingClientRect();
      const containerRect = listContainer.getBoundingClientRect();
      
      const elementTopRelative = elementRect.top - containerRect.top;
      const elementBottomRelative = elementRect.bottom - containerRect.top;
      
      const padding = 50;
      
      if (elementTopRelative < 0) {
        listContainer.scrollBy({
          top: elementTopRelative - padding,
          behavior: 'smooth'
        });
      } 
      else if (elementBottomRelative > containerRect.height) {
        listContainer.scrollBy({
          top: elementBottomRelative - containerRect.height + padding,
          behavior: 'smooth'
        });
      }
    }
  };

  // Load map component
  useEffect(() => {
    let mounted = true;

    const loadComponent = async () => {
      try {
        const module = await import(`../../clients/${authCtx.league}/${authCtx.venue}/maps/${currentLocation.mapId}.js`);
        if (mounted) {
          setLoadedComponent(() => module.default);
          setAnalyticsConfig(module.ANALYTICS_CONFIG || null);
        }
      } catch (error) {
        console.error('Error loading map component:', error);
      }
    };

    if (authCtx.venue && currentLocation.mapId) {
      loadComponent();
    }

    return () => {
      mounted = false;
    };
  }, [authCtx.venue, authCtx.league, currentLocation.mapId]);

  // Enable occupancy auto-refresh when today is selected
  useEffect(() => {
    if (isToday) {
      setOccupancyAutoFetchEnabled(true);
      setOccupancyAutoRefreshEnabled(true);
    } else {
      setOccupancyAutoRefreshEnabled(false);
    }
  }, [isToday, setOccupancyAutoFetchEnabled]);

  // Update current time based on today's date when component mounts or refreshes
  useEffect(() => {
    if (isToday) {
      let currentMinutes = Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000) - 1;
      
      const currentHour = moment().tz(currentLocation.timeZone).hour();
      if (currentHour >= 0 && currentHour < 3) {
        currentMinutes = 1440 + currentMinutes;
      }
      
      const endTime = Math.max(doorsOpenTimeInMins, currentMinutes);
      setCurrentTime(endTime);
    } else {
      setCurrentTime(doorsOpenTimeInMins);
    }
  }, [isToday, dayType, currentLocation, setCurrentTime, doorsOpenTimeInMins]);

  // Live day: re-render every 15s so "now" (the slider's end) advances smoothly
  const [, setClockTick] = useState(0);
  useEffect(() => {
    if (!isToday) return undefined;
    const id = setInterval(() => setClockTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, [isToday]);

  // Find cutoff time using util function
  useEffect(() => {
    if (isAnalyticsView && data) {
      const startTimeMinutes = doorsOpenTimeInMins;
      
      const rawCutoffTime = findIntelligentCutoff(data, startTimeMinutes);
      
      const currentHour = isToday ? 
        Math.floor(Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000) / 60) : 
        -1;
      
      const cutoffHour = Math.floor(rawCutoffTime / 60);
      
      let finalCutoffTime = rawCutoffTime;
      if (isToday && currentHour === cutoffHour) {
        finalCutoffTime = Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000);
      }
      
      const roundedCutoffTime = roundToNextHour(finalCutoffTime);
      setCutoffTime(roundedCutoffTime);
    }
  }, [data, isAnalyticsView, doorsOpenTimeInMins, isToday, currentLocation.timeZone]);

  // Prepare average data for analytics view
  useEffect(() => {
    if (isAnalyticsView && data && cutoffTime) {
      const totalTraffic = {};
      const countPerZone = {};
      
      const startTimeMinutes = doorsOpenTimeInMins;
      const endTimeMinutes = cutoffTime;
      
      Object.entries(data).forEach(([timeKey, timeSlot]) => {
        const minutesValue = parseInt(timeKey, 10);
        
        if (minutesValue >= startTimeMinutes && minutesValue <= endTimeMinutes) {
          if (timeSlot && timeSlot.traffic && Array.isArray(timeSlot.traffic)) {
            timeSlot.traffic.forEach(zone => {
              if (zone && zone.id !== undefined) {
                if (!totalTraffic[zone.id]) {
                  totalTraffic[zone.id] = 0;
                  countPerZone[zone.id] = 0;
                }
                totalTraffic[zone.id] += zone.traffic || 0;
                countPerZone[zone.id]++;
              }
            });
          }
        }
      });
      
      const averageTraffic = {};
      Object.keys(totalTraffic).forEach(zoneId => {
        if (countPerZone[zoneId] > 0) {
          averageTraffic[zoneId] = Math.round(totalTraffic[zoneId] / countPerZone[zoneId]);
        } else {
          averageTraffic[zoneId] = 0;
        }
      });
      
      const formattedAverageData = {
        total: Math.round(
          Object.values(averageTraffic).reduce((sum, value) => sum + value, 0)
        ),
        perc: Math.round(
          (Object.values(averageTraffic).reduce((sum, value) => sum + value, 0) / currentLocation.maxOccupancy) * 100
        ),
        traffic: Object.entries(averageTraffic).map(([id, traffic]) => ({
          id: parseInt(id),
          traffic: traffic
        }))
      };
      
      setAverageData(formattedAverageData);
    }
  }, [data, isAnalyticsView, currentLocation.maxOccupancy, doorsOpenTimeInMins, cutoffTime]);

  // Calculate estimated dwell time per zone using adjacency-enhanced Little's Law
  // Dwell Time (W) = Average Occupancy (L) / Estimated Throughput (λ)
  // Output shape: { [zoneId]: { avg, peak, peakMinute } }
  useEffect(() => {
    if (!isAnalyticsView || !data || !cutoffTime) return;

    const startTimeMinutes = doorsOpenTimeInMins;

    // Determine the end of the dwell calculation window
    // If today: use current time (data is still coming in, intelligent cutoff is unreliable)
    // If not today: use cutoffTime but enforce a minimum so early-day dips don't trigger false cutoffs
    let endTimeMinutes;
    if (isToday) {
      // Use current time as the end — the game is still in progress
      let currentMinutes = Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000);
      const currentHour = moment().tz(currentLocation.timeZone).hour();
      if (currentHour >= 0 && currentHour < 3) {
        currentMinutes = 1440 + currentMinutes;
      }
      endTimeMinutes = currentMinutes;
    } else {
      // Enforce minimum cutoff: never cut off earlier than 1 PM (minute 780)
      // This prevents mid-day traffic lulls from being mistaken for store closing
      const MIN_CUTOFF = 780;
      endTimeMinutes = Math.max(cutoffTime, MIN_CUTOFF);
    }

    // Need at least 15 minutes of data for meaningful estimates
    if (endTimeMinutes - startTimeMinutes < 15) return;

    const estimates = {};

    const sampleTraffic = Object.values(data).find(d => d?.traffic)?.traffic;
    if (!sampleTraffic) return;

    const zoneIds = sampleTraffic.map(z => z.id);

    // Read adjacency map directly from location config
    const adjacencyMap = currentLocation.zoneAdjacency || null;

    // Build time series for all zones
    const allSeries = {};
    zoneIds.forEach(zoneId => {
      const series = [];
      for (let t = startTimeMinutes; t <= endTimeMinutes; t++) {
        const entry = data[t]?.traffic?.find(z => z.id === zoneId);
        series.push(entry?.traffic || 0);
      }
      allSeries[zoneId] = series;
    });

    // Compute deltas for all zones at each time step
    const allDeltas = {};
    zoneIds.forEach(zoneId => {
      const series = allSeries[zoneId];
      const deltas = [0];
      for (let i = 1; i < series.length; i++) {
        deltas.push(series[i] - series[i - 1]);
      }
      allDeltas[zoneId] = deltas;
    });

    // Rolling window size for peak dwell calculation (in minutes)
    const WINDOW_SIZE = 15;

    // Estimate throughput per zone
    zoneIds.forEach(zoneId => {
      const series = allSeries[zoneId];
      const deltas = allDeltas[zoneId];

      if (series.length < 2) {
        estimates[zoneId] = { avg: null, peak: null, peakMinute: null };
        return;
      }

      // Compute per-step flow for this zone
      const stepFlow = [0];
      if (adjacencyMap && adjacencyMap[zoneId]) {
        const neighbors = adjacencyMap[zoneId];

        for (let i = 1; i < deltas.length; i++) {
          const ownDelta = deltas[i];
          const ownFlow = Math.abs(ownDelta);

          let adjacencyFlow = 0;

          neighbors.forEach(neighborId => {
            if (!allDeltas[neighborId]) return;
            const neighborDelta = allDeltas[neighborId][i];

            if (ownDelta < 0 && neighborDelta > 0) {
              adjacencyFlow += Math.min(Math.abs(ownDelta), neighborDelta);
            } else if (ownDelta > 0 && neighborDelta < 0) {
              adjacencyFlow += Math.min(ownDelta, Math.abs(neighborDelta));
            } else if (ownDelta === 0 && neighborDelta !== 0) {
              const neighborConnections = adjacencyMap[neighborId]?.length || 1;
              adjacencyFlow += Math.abs(neighborDelta) / neighborConnections;
            }
          });

          stepFlow.push(Math.max(ownFlow, adjacencyFlow));
        }
      } else {
        // Fallback: no adjacency
        for (let i = 1; i < deltas.length; i++) {
          stepFlow.push(Math.abs(deltas[i]));
        }
      }

      // Average dwell (only counting minutes with meaningful flow)
      const MIN_FLOW_THRESHOLD = 0.5;
      let totalOccupancy = 0;
      let totalFlow = 0;
      let activeSteps = 0;

      for (let i = 0; i < series.length; i++) {
        if (stepFlow[i] >= MIN_FLOW_THRESHOLD) {
          totalOccupancy += series[i];
          totalFlow += stepFlow[i];
          activeSteps++;
        }
      }

      const avgOccupancy = activeSteps > 0 ? totalOccupancy / activeSteps : 0;
      const flowRate = activeSteps > 0 ? totalFlow / activeSteps : 0;

      const avgDwell = flowRate > 0 ? Math.round(avgOccupancy / flowRate) : null;

      // Peak dwell (rolling window)
      let peakDwell = null;
      let peakMinuteIndex = null;

      if (series.length >= WINDOW_SIZE) {
        for (let start = 0; start <= series.length - WINDOW_SIZE; start++) {
          let windowOccupancy = 0;
          let windowFlow = 0;

          for (let j = start; j < start + WINDOW_SIZE; j++) {
            windowOccupancy += series[j];
            windowFlow += stepFlow[j];
          }

          const windowAvgOcc = windowOccupancy / WINDOW_SIZE;
          const windowFlowRate = windowFlow / WINDOW_SIZE;

          if (windowFlowRate > 0.5) {
            const windowDwell = Math.round(windowAvgOcc / windowFlowRate);
            if (peakDwell === null || windowDwell > peakDwell) {
              peakDwell = windowDwell;
              peakMinuteIndex = start + Math.floor(WINDOW_SIZE / 2);
            }
          }
        }
      }

      // Convert index back to actual minute
      const peakMinute = peakMinuteIndex !== null
        ? startTimeMinutes + peakMinuteIndex
        : null;

      estimates[zoneId] = {
        avg: avgDwell,
        peak: peakDwell,
        peakMinute: peakMinute
      };
    });

    setZoneDwellEstimates(estimates);

  }, [data, isAnalyticsView, doorsOpenTimeInMins, cutoffTime, isToday, currentLocation.zoneAdjacency, currentLocation.timeZone]);

  useEffect(() => {
    if (!data) return;

    if (isAnalyticsView && averageData) {
      setCurrentData(averageData);
      return;
    }
    
    if (!data[currentTime]) {
      console.warn(`No data found for time ${currentTime}`);
      const timeKeys = Object.keys(data).map(Number).sort((a, b) => a - b);
      if (timeKeys.length === 0) return;
      
      const nearestTime = timeKeys.reduce((prev, curr) => 
        Math.abs(curr - currentTime) < Math.abs(prev - currentTime) ? curr : prev, timeKeys[0]);
      
      console.log(`Using nearest available time: ${nearestTime}`);
      setCurrentData(data[nearestTime]);
      return;
    }
    
    setCurrentData(data[currentTime]);
    
    if (occupancyAutoFetchDidComplete) {
      setOccupancyAutoFetchDidComplete(false);
    }
  }, [
    data, 
    currentTime, 
    averageData, 
    isAnalyticsView, 
    occupancyAutoFetchDidComplete, 
    setOccupancyAutoFetchDidComplete
  ]);

  // Calculate the maximum slider value with extended day concept
  const maxSliderValue = isToday
    ? (() => {
        let currentMinutes = Math.round((moment().tz(currentLocation.timeZone) - moment().tz(currentLocation.timeZone).startOf("day")) / 60000) - 1;
        
        const currentHour = moment().tz(currentLocation.timeZone).hour();
        if (currentHour >= 0 && currentHour < 3) {
          currentMinutes = 1440 + currentMinutes;
        }
        
        return Math.max(doorsOpenTimeInMins, currentMinutes);
      })()
    : dayType === currentLocation.dayTypes.GAME 
    ? 1440 + 180
    : 1440;

  // While following live, keep the handle on "now" as soon as it advances
  useEffect(() => {
    if (isToday && occupancyAutoRefreshEnabled && currentTime !== maxSliderValue) {
      setCurrentTime(maxSliderValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, occupancyAutoRefreshEnabled, maxSliderValue]);

  const handleSliderChange = (_, value) => {
    setCurrentTime(value);
    
    // Within a minute of the end counts as live ("now" may have just ticked over)
    if (value >= maxSliderValue - 1) {
      setOccupancyAutoRefreshEnabled(true);
      setOccupancyAutoFetchEnabled(true);
    } 
    else if (occupancyAutoRefreshEnabled) {
      setOccupancyAutoRefreshEnabled(false);
      setOccupancyAutoFetchEnabled(false);
    }
  };

  // Latest values for the play loop (about 20 seconds from start to end)
  playRef.current = {
    change: handleSliderChange,
    time: currentTime,
    min: doorsOpenTimeInMins,
    max: maxSliderValue,
    step: Math.max(1, Math.round((maxSliderValue - doorsOpenTimeInMins) / 200)),
  };
  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    if ((currentTime ?? doorsOpenTimeInMins) >= maxSliderValue) handleSliderChange(null, doorsOpenTimeInMins);
    setPlaying(true);
  };

  const calculateSliderMarks = () => {
    const marks = [{ 
      value: doorsOpenTimeInMins, 
      label: moment().startOf('day').add(doorsOpenTimeInMins, 'minutes').format('h:mm A') 
    }];

    if (isToday) {
      let currentMinutes = getCurrentMinute();
      
      const currentHour = moment().tz(currentLocation.timeZone).hour();
      if (currentHour >= 0 && currentHour < 3) {
        currentMinutes = 1440 + currentMinutes;
      }
      
      marks.push({
        value: currentMinutes,
        label: currentMinutes >= 1440 
          ? moment().add(1, 'day').startOf('day').add(currentMinutes - 1440, 'minutes').format('h:mm A')
          : moment().startOf('day').add(currentMinutes, 'minutes').format('h:mm A')
      });
    } else {
      marks.push({
        value: 1439,
        label: "12:00 AM",
      });
      marks.push({
        value: 1620,
        label: "3:00 AM",
      });
    }

    return marks.filter(Boolean);
  };

  const getFloorOccupancy = (floorId) => {
    if (!currentData?.traffic || !currentLocation?.zones) return 0;
    
    const floorZoneIds = currentLocation.zones
      .filter(z => z.floor === floorId)
      .map(z => z.id);
    
    return currentData.traffic
      .filter(t => floorZoneIds.includes(t.id))
      .reduce((sum, t) => sum + (t.traffic || 0), 0);
  };

  const getFloorPercentage = (floorId) => {
    const floorConfig = currentLocation.floors?.find(f => f.id === floorId);
    if (!floorConfig?.maxOccupancy) return 0;
    
    const occupancy = getFloorOccupancy(floorId);
    return Math.round((occupancy / floorConfig.maxOccupancy) * 100);
  };

  const formatSecondsToMinSec = (secs) => {
    if (secs === null || secs === undefined) return '--';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDwellPeakTime = (minute) => {
    if (minute === null || minute === undefined) return null;
    if (minute >= 1440) {
      return moment().add(1, 'day').startOf('day').add(minute - 1440, 'minutes').format('h:mm A');
    }
    return moment().startOf('day').add(minute, 'minutes').format('h:mm A');
  };

  const computeMiniMapStats = (miniMapData, shapes) => {
    if (!miniMapData || !shapes || shapes.length === 0) return {};
    
    const allMinuteKeys = Object.keys(miniMapData).map(Number);
    const stats = {};
    
    shapes.forEach(shape => {
      const values = [];
      let peakVal = -Infinity;
      let peakMinute = null;
      
      allMinuteKeys.forEach(minute => {
        const val = miniMapData[minute]?.[shape.id];
        if (val !== undefined && val !== null) {
          values.push(val);
          if (val > peakVal) {
            peakVal = val;
            peakMinute = minute;
          }
        }
      });
      
      stats[shape.id] = {
        peak: values.length > 0 ? peakVal : null,
        avg: values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : null,
        peakMinute: peakMinute
      };
    });
    
    return stats;
  };

  if (!loadedComponent) {
    return <div className="cx-skel rx-skel-block" style={{ height: isAnalyticsView ? 640 : 600 }} aria-busy="true" aria-label="Loading map component" />;
  }

  if (!data) {
    return <div className="cx-skel rx-skel-block" style={{ height: isAnalyticsView ? 640 : 600 }} aria-busy="true" aria-label="Loading occupancy data" />;
  }

  if (currentTime === null || currentTime === undefined) {
    return <div className="cx-skel rx-skel-block" style={{ height: isAnalyticsView ? 640 : 600 }} aria-busy="true" aria-label="Initializing time data" />;
  }

  const MapComponent = loadedComponent;

  if (isAnalyticsView) {
    const zoneData = currentData?.traffic || [];
    const sortedZones = [...zoneData].sort((a, b) => b.traffic - a.traffic);
    const rangeLabel = `${moment().startOf('day').add(doorsOpenTimeInMins, 'minutes').format('h:mm A')} – ${moment().startOf('day').add(cutoffTime, 'minutes').format('h:mm A')}`;
    const allDwells = Object.values(zoneDwellEstimates).map((v) => v?.avg).filter((v) => v != null);
    const maxDwell = allDwells.length ? Math.max(...allDwells) : 0;

    const avgValues = sortedZones.map((z) => z.traffic || 0);

    return (
      <div className="rx-hero rx-analytics">
        <div className="rx-hero-body">
          <div className="rx-hero-map">
            <div className="rx-hero-caption">
              <span className="rx-hero-caption-title">Average occupancy</span>
              <span className="rx-hero-caption-sub">{rangeLabel}</span>
            </div>
            <div className="rx-map-canvas">
              <MapComponent
                currentLocation={currentLocation}
                currentData={currentData}
                findLabelByZoneId={findLabelByZoneId}
                isAnalyticsView={isAnalyticsView}
                hoveredZoneId={hoveredZoneId}
                onZoneHover={handleZoneHover}
              />
            </div>
          </div>

          <div className="rx-hero-zones" ref={zoneListRef}>
            {sortedZones.length > 0 ? (
              sortedZones.map((zone) => {
                const dwell = zoneDwellEstimates[zone.id];
                const dwellColor = dwell?.avg != null ? dwellHeatColor(dwell.avg, allDwells) : null;
                return (
                  <div
                    key={zone.id}
                    ref={(el) => { zoneRefs.current[zone.id] = el; }}
                    className={`rx-zone-card${zone.id === hoveredZoneId ? ' active' : ''}`}
                    style={{ '--zone-heat': zoneHeatColor(zone.traffic, avgValues, true) }}
                    onMouseEnter={() => setHoveredZoneId(zone.id)}
                    onMouseLeave={() => setHoveredZoneId(null)}
                  >
                    <div className="rx-zone-top">
                      <span className="rx-zone-name">
                        <span className="rx-zone-id">Zone {zone.id}</span>
                        <span className="rx-zone-label">{findLabelByZoneId(zone.id)}</span>
                      </span>
                      <span className="rx-zone-value">
                        {zone.traffic}
                        <Icon name="person-standing" />
                      </span>
                    </div>
                    {dwell?.avg != null && (
                      <div className="rx-dwell">
                        <div className="rx-dwell-head">
                          <span><Icon name="clock" /> Avg. est. dwell time</span>
                          <strong>~{dwell.avg} min</strong>
                        </div>
                        <div className="rx-dwell-bar">
                          <span style={{ width: `${maxDwell > 0 ? Math.round((dwell.avg / maxDwell) * 100) : 0}%`, background: dwellColor }} />
                        </div>
                        {dwell.peak != null && (
                          <div className="rx-dwell-peak">
                            <span>Peak dwell</span>
                            <span>~{dwell.peak} min{dwell.peakMinute != null ? ` at ${formatDwellPeakTime(dwell.peakMinute)}` : ''}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="cx-empty">No zone data available</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- Live (non-analytics) layout: single and multi-floor stores ----
  const isMultiFloor = currentLocation.floors && currentLocation.floors.length > 1;
  const queueStats = computeMiniMapStats(queueTimeDataMiniMap, currentLocation.queueZone?.shapes);
  const transactionStats = computeMiniMapStats(transactionTimeDataMiniMap, currentLocation.transactionZone?.shapes);
  const isGameDay = dayType === "game";

  const minuteLabel = (minute) =>
    minute >= 1440
      ? moment().add(1, 'day').startOf('day').add(minute - 1440, 'minutes').format('h:mm A')
      : moment().startOf('day').add(minute, 'minutes').format('h:mm A');

  const storeClosed = isToday && (() => {
    let nowMinutes = moment().tz(currentLocation.timeZone).hours() * 60 + moment().tz(currentLocation.timeZone).minutes();
    if (moment().tz(currentLocation.timeZone).hour() < 3) nowMinutes += 1440;
    return doorsOpenTimeInMins > nowMinutes;
  })();
  const atLive = isToday && (occupancyAutoRefreshEnabled || currentTime >= maxSliderValue - 1);

  const total = currentData?.total;
  const perc = currentData?.perc;
  const atCapacity = (perc || 0) > 99;

  // Game days: gates open and game start as marks on the slider, labeled under it like the other times
  const withGameMarks = (marks) => {
    if (!isGameDay) return marks;
    const toMinute = (hhmm) => {
      if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
      const [h, m] = hhmm.split(':').map(Number);
      return Number.isNaN(h) || Number.isNaN(m) ? null : (h < 3 ? h + 24 : h) * 60 + m;
    };
    const span = Math.max(1, maxSliderValue - doorsOpenTimeInMins);
    const extra = [
      { value: toMinute(currentGame?.gatesOpen), title: 'Gates open' },
      { value: toMinute(currentGame?.gameStartTime), title: 'Game start' },
    ].filter((m) => m.value != null && m.value > doorsOpenTimeInMins && m.value <= maxSliderValue);
    // Drop an edge time's label when it would sit on top of gates or game start
    const crowded = (v) => extra.some((e) => Math.abs(e.value - v) / span < 0.07);
    return [
      ...marks.map((m) => (crowded(m.value) ? { value: m.value } : m)),
      ...extra.map((e) => ({
        value: e.value,
        label: (
          <span className="rx-mark-label">
            <span>{e.title}</span>
            {minuteLabel(e.value)}
          </span>
        ),
      })),
    ];
  };
  const baseSliderMarks = calculateSliderMarks();
  const sliderMarks = withGameMarks(baseSliderMarks);
  // Gates open and game start are the last marks; draw them as ticks across the line
  const gameTickSx = Object.fromEntries(
    sliderMarks.slice(baseSliderMarks.length).map((_, i) => [
      `& .MuiSlider-mark[data-index="${baseSliderMarks.length + i}"]`,
      { width: '3px', height: '18px', borderRadius: '2px', backgroundColor: '#3d4847', opacity: 1, transform: 'translate(-50%, -50%)' },
    ])
  );

  const peakBlock = (p) => (p ? { total: p.total || 0, perc: p.perc || 0, at: p.time2 ? moment(p.time2).format('LT') : null } : null);
  const peakPre = isGameDay ? peakBlock(peak?.preGame) : null;
  const peakMid = isGameDay ? peakBlock(peak?.midGame) : null;
  const peakDay = !isGameDay ? peakBlock(peak?.wholeDay) : null;
  const peakMain = isGameDay ? (peakMid && peakMid.total >= (peakPre?.total || 0) ? peakMid : peakPre) : peakDay;
  const avgMain = isGameDay ? (avg?.startGame || avg?.preGame || 0) : (avg?.wholeDay || 0);

  // The window the average covers (same windows the controller averages over)
  const shortClock = (minute) => minuteLabel(minute).replace(':00', '');
  const avgRange = (() => {
    if (!isGameDay) {
      const open = currentLocation.earliestOpenTime;
      return typeof open === 'number' ? `since ${shortClock(open)}` : '';
    }
    const gatesMoment = getGatesOpenTime ? getGatesOpenTime() : null;
    if (!gatesMoment || !gatesMoment.isValid || !gatesMoment.isValid()) return '';
    const gatesMin = gatesMoment.hours() * 60 + gatesMoment.minutes();
    if (avg?.startGame) {
      // Gates open until the store quiets down (rounded up to the hour), or now on a live day
      if (isToday) return `${shortClock(gatesMin)} – now`;
      const end = roundToNextHour(findIntelligentCutoff(data, gatesMin - 1));
      return `${shortClock(gatesMin)} – ${shortClock(end)}`;
    }
    // Only the pregame average exists: pregame start until gates open
    const pre = currentGame?.pregameStart;
    if (pre && pre.includes(':')) {
      const [h, m] = pre.split(':').map(Number);
      return `${shortClock(h * 60 + m)} – ${shortClock(gatesMin)}`;
    }
    return `before ${shortClock(gatesMin)}`;
  })();

  const zoneList = [...(currentData?.traffic || [])]
    .map((z) => ({ id: z.id, value: z.traffic || 0, label: findLabelByZoneId ? findLabelByZoneId(z.id) : '' }))
    .sort((a, b) => b.value - a.value);
  const busiest = zoneList[0];


  const hasQueueMap = !!currentLocation.queueZone;
  const hasCheckoutMap = !!currentLocation.transactionZone;
  // Queue and checkout mini-maps get their own narrow column between the map and the rail
  const hasMiniMaps = hasQueueMap || hasCheckoutMap;

  // The reading for the slider's minute (data comes in every minute)
  const readingAt = (byMinute) => (byMinute ? byMinute[currentTime] ?? null : null);

  // Compact queue / checkout view for the rail: the mini-map, then each line's peak and average
  const renderRailMiniMap = (type, zoneConfig, stats, timeData, format, title) => (
    <div className="rx-rail-minimap rx-mini-card">
      <div className="rx-mini-title">
        {title}
        <span>{zoneConfig?.unit ? `(${zoneConfig.unit})` : ''}</span>
      </div>
      <div className="rx-rail-minimap-map">
        <MiniMapZone type={type} currentLocation={currentLocation} timeData={timeData} compact />
      </div>
      {(zoneConfig?.shapes || []).map((shape) => {
        const st = stats[shape.id];
        return (
          <div
            key={shape.id}
            className="rx-rail-row"
            title={st?.peakMinute != null ? `Peak at ${minuteLabel(st.peakMinute)}` : undefined}
          >
            <span className="rx-rail-row-name">{shape.label}</span>
            <span className="rx-rail-row-stat"><small>Peak</small> {format(st?.peak)}</span>
            <span className="rx-rail-row-stat"><small>Avg</small> {format(st?.avg)}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="rx-live rx-live-compact">
      {/* One screen: map on the left, numbers and lists in the rail on the right */}
      <div className="rx-hero" ref={heroRef}>
        <div className={`rx-hero-body rx-hero-grid${hasMiniMaps ? ' rx-hero-grid-3' : ''}`}>
          <div className="rx-hero-map">
            <div className="rx-map-canvas">
              <MapComponent
                currentLocation={currentLocation}
                currentData={currentData}
                findLabelByZoneId={findLabelByZoneId}
                onZoneHover={handleZoneHover}
                hoveredZoneId={hoveredZoneId}
                isAnalyticsView={isAnalyticsView}
                availableHeight={mapFit}
              />
            </div>
          </div>

          {hasMiniMaps && (
            <div className="rx-mini-col">
              {hasQueueMap && renderRailMiniMap(
                'queue', currentLocation.queueZone, queueStats, readingAt(queueTimeDataMiniMap),
                (v) => (v !== null && v !== undefined ? `${v} min` : '--'), 'Queue time'
              )}
              {hasCheckoutMap && renderRailMiniMap(
                'transaction', currentLocation.transactionZone, transactionStats, readingAt(transactionTimeDataMiniMap),
                formatSecondsToMinSec, 'Checkout time'
              )}
            </div>
          )}

          <aside className="rx-rail">
            <div className="rx-rail-stats">
              <div className={`rx-rail-stat rx-rail-stat-main${atCapacity ? ' at-capacity' : ''}`}>
                <span className="rx-rail-stat-label">
                  {atLive ? <><span className="rx-map-tag-dot" />In store now</> : `In store at ${minuteLabel(currentTime)}`}
                </span>
                <span className="rx-rail-stat-value">
                  {typeof total === 'number' ? total : '--'}
                  <small>/ {currentLocation.maxOccupancy}</small>
                </span>
                <span className="rx-rail-stat-sub">
                  {atCapacity && <Icon name="exclamation-circle-fill" />}
                  {typeof perc === 'number' ? `${perc}% full` : '--'}
                </span>
              </div>
              <div className="rx-rail-stat">
                <span className="rx-rail-stat-label">Peak</span>
                <span className="rx-rail-stat-value">{peakMain?.total ?? 0}</span>
                <span className="rx-rail-stat-sub">{peakMain?.at ? `at ${peakMain.at}` : ''}</span>
              </div>
              <div className="rx-rail-stat">
                <span className="rx-rail-stat-label">Average</span>
                <span className="rx-rail-stat-value">{avgMain}</span>
                <span className="rx-rail-stat-sub">{avgRange}</span>
              </div>
              <div className="rx-rail-stat">
                <span className="rx-rail-stat-label">{atLive ? 'Busiest zone now' : 'Busiest zone'}</span>
                <span className="rx-rail-stat-value rx-rail-stat-zone">{busiest ? (busiest.label || `Zone ${busiest.id}`) : '--'}</span>
                <span className="rx-rail-stat-sub">{busiest ? `Zone ${busiest.id} · ${busiest.value} people` : 'No zones'}</span>
              </div>
            </div>

            {isMultiFloor && (
              <div className="rx-floors rx-rail-floors">
                {currentLocation.floors.map((floor) => {
                  const occ = getFloorOccupancy(floor.id);
                  const pct = getFloorPercentage(floor.id);
                  return (
                    <div key={floor.id} className="rx-floor-row">
                      <span className="rx-floor-name">{floor.label}</span>
                      <span className="rx-floor-bar"><span style={{ width: `${Math.min(100, pct)}%` }} /></span>
                      <span className="rx-floor-val">
                        {pct > 99 && <Icon name="exclamation-circle-fill" className="rx-floor-warn" />}
                        {occ}<small> / {floor.maxOccupancy}</small>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="rx-rail-body" ref={zoneListRef}>
              {zoneList.map((z) => {
                const p = zonePeaks[z.id];
                return (
                  <div
                    key={z.id}
                    ref={(el) => { zoneRefs.current[z.id] = el; }}
                    className={`rx-zone-card rx-zone-card-compact${hoveredZoneId === z.id ? ' active' : ''}`}
                    style={{ '--zone-heat': zoneHeatColor(z.value, null, false) }}
                    onMouseEnter={() => setHoveredZoneId(z.id)}
                    onMouseLeave={() => setHoveredZoneId(null)}
                  >
                    <div className="rx-zone-top">
                      <span className="rx-zone-name">
                        <span className="rx-zone-label">{z.label || `Zone ${z.id}`}</span>
                        <span className="rx-zone-id">
                          Zone {z.id}
                          {p && p.value > 0 && <> · peak {p.value} at {formatClock(p.t)}</>}
                        </span>
                      </span>
                      <span className="rx-zone-value">
                        {z.value}
                        <Icon name="person-standing" />
                      </span>
                    </div>
                  </div>
                );
              })}
              {zoneList.length === 0 && <div className="cx-empty">No zone data yet</div>}
            </div>
          </aside>
        </div>
      </div>

      {/* Time controls, pinned to the bottom of the screen while the Map tab scrolls */}
      {storeClosed ? (
        <div className={`rx-dock rx-scrubber-closed${dockSettled ? ' settled' : ''}`}>
          <Icon name="moon-stars" /> Store closed: opens at {minuteLabel(doorsOpenTimeInMins)}
        </div>
      ) : (
        <div className={`rx-dock${dockSettled ? ' settled' : ''}`}>
          <div className="rx-scrubber-head">
            <button
              type="button"
              className={`rx-play${playing ? ' playing' : ''}`}
              onClick={togglePlay}
              aria-label={playing ? 'Pause' : 'Play through the day'}
              title={playing ? 'Pause' : 'Play through the day'}
            >
              <Icon name={playing ? 'pause-fill' : 'play-fill'} />
            </button>
            {isToday && (
              // Fixed-width slot so swapping Live / Jump to live never resizes the slider
              <div className="rx-live-slot">
                {atLive ? (
                  <span className="rx-scrubber-live">Live</span>
                ) : (
                  <button
                    type="button"
                    className="cx-sort-btn"
                    onClick={() => { setPlaying(false); handleSliderChange(null, maxSliderValue); }}
                  >
                    <Icon name="skip-end-fill" /> Jump to live
                  </button>
                )}
              </div>
            )}
          </div>
          <Slider
            onChange={(e, v) => { if (playing) setPlaying(false); handleSliderChange(e, v); }}
            value={currentTime || doorsOpenTimeInMins}
            step={1}
            min={doorsOpenTimeInMins}
            max={maxSliderValue}
            marks={sliderMarks}
            valueLabelDisplay="on"
            valueLabelFormat={(v) => minuteLabel(v)}
            className="rx-slider"
            sx={gameTickSx}
          />
        </div>
      )}
      <div ref={dockSentinelRef} className="rx-dock-sentinel" aria-hidden="true" />
    </div>
  );
};

export default RetailMap;